<div class="settlement-agent-container">
  @if (isLoading) {
    <div class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  }

  <form #settlementAgentForm="ngForm" class="settlement-agent-form">
    <div class="section-header mb-3 d-flex justify-content-between align-items-center">
      <h5>Settlement Agent Information <span class="text-danger">*</span></h5>
      <div class="d-flex align-items-center gap-2">
        @if (copySourceProvider) {
          <div ngbDropdown container="body" placement="bottom-end" (openChange)="onCopyFromToggled($event)">
            <button type="button" class="btn btn-sm btn-outline-secondary" ngbDropdownToggle
                    [ngbTooltip]="'Fill these fields from another party on this request'" placement="left">
              <i class="fa-solid fa-paste me-1"></i> Copy From
            </button>
            <div ngbDropdownMenu>
              @for (source of copySources; track source.key) {
                <button type="button" ngbDropdownItem (click)="copyFrom(source)">{{ source.label }}</button>
              }
              @if (!copySources.length) {
                <span class="dropdown-item-text text-muted small">No other party has been filled out yet</span>
              }
            </div>
          </div>
        }
        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="copyContactInfo()"
                [ngbTooltip]="'Copy contact info to clipboard'" placement="left">
          <i class="fa-solid fa-copy me-1"></i> Copy
        </button>
      </div>
    </div>

    <div class="section-subheader mb-3">
      <h6>Company Information</h6>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="companyName">Company Name</label>
          <div class="col-md-9">
            <text-input
                   id="companyName"
                   name="companyName"
                   [(ngModel)]="formData.companyName"
                   maxlength="100"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="companyLicenseId">Company License ID</label>
          <div class="col-md-9">
            <text-input
                   id="companyLicenseId"
                   name="companyLicenseId"
                   [(ngModel)]="formData.companyLicenseId"
                   maxlength="50"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="address">Address</label>
          <div class="col-md-9">
            <text-input
                   id="address"
                   name="address"
                   [(ngModel)]="formData.address"
                   maxlength="200"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="city">City</label>
          <div class="col-md-9">
            <text-input
                   id="city"
                   name="city"
                   [(ngModel)]="formData.city"
                   maxlength="100"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="state">State</label>
          <div class="col-md-9">
            <select
              class="form-select"
              [(ngModel)]="formData.state"
              id="state"
              name="state">
              <option [ngValue]="''">-- Select State --</option>
              @for (state of states; track state) {
                <option [ngValue]="state.value">
                  {{ state.name }}
                </option>
              }
            </select>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="zip">Zip</label>
          <div class="col-md-9">
            <text-input
                   id="zip"
                   name="zip"
                   [(ngModel)]="formData.zip"
                   maxlength="20"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>
    </div>

    <div class="section-subheader mb-3">
      <h6>Contact Information</h6>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="contactName">Contact Name</label>
          <div class="col-md-9">
            <text-input
                   id="contactName"
                   name="contactName"
                   [(ngModel)]="formData.contactName"
                   maxlength="100"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="contactLicense">Contact License</label>
          <div class="col-md-9">
            <text-input
                   id="contactLicense"
                   name="contactLicense"
                   [(ngModel)]="formData.contactLicense"
                   maxlength="50"
                   [required]="true">
            </text-input>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="contactEmail">Contact Email</label>
          <div class="col-md-9">
            <text-input
                   id="contactEmail"
                   name="contactEmail"
                   type="email"
                   [(ngModel)]="formData.contactEmail"
                   [ngClass]="{'is-invalid': contactEmailField && contactEmailField.touched && contactEmailField.invalid}"
                   #contactEmailField="ngModel"
                   maxlength="100"
                   email
                   [required]="true">
            </text-input>
            <div class="invalid-feedback">
              Please enter a valid email address
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="row">
          <label class="col-md-3 col-form-label" for="contactPhone">Contact Phone</label>
          <div class="col-md-9">
            <input class="form-control"
                   id="contactPhone"
                   name="contactPhone"
                   #contactPhone="ngModel"
                   [(ngModel)]="formData.contactPhone"
                   [ngClass]="{'is-invalid': contactPhone && contactPhone.touched && contactPhone.invalid}"
                   [mask]="'(000) 000-0000'"
                   [showMaskTyped]="true"
                    [required]="true"/>
            <div class="invalid-feedback">
              Please enter a valid phone number.
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>
